import React, { useEffect, useState } from "react";
import { View, StyleSheet, ActivityIndicator, useWindowDimensions } from "react-native";
import CustomText from "../../../components/common/general/CustomText/CustomText";
import CustomButton from "../../../components/common/general/CustomButton/CustomButton";
import CustomSpacing from "../../../components/common/layout/CustomSpacing/CustomSpacing";
import DatePickerInput from "react-native-paper-dates/src/Date/DatePickerInput";
import { SparkleIcon, CalendarBlankIcon } from "@phosphor-icons/react";
import { Calendar } from "@phosphor-icons/react/dist/csr/Calendar";
import { z } from "zod";
import { zodResolver } from "@hookform/resolvers/zod";
import { Controller, useForm } from "react-hook-form";
import {
  useGenerateDueDatesForActivitiesMutation,
  useGetCourseCoreQuery,
  useUpdateCourseDatesMutation,
} from "../../../graphql/generated/graphql";
import NewCustomAxioActionModal from "../../settings/UsersTabContainer/NewCustomAxioActionModal";
import { useAppConfig } from "../../../AppConfigProvider";

enum ModalState {
  CLOSED = "CLOSED",
  PROCESSING = "PROCESSING",
  SUCCESS = "SUCCESS",
  ERROR = "ERROR",
  GENERATION_ERROR = "GENERATION_ERROR",
}

const modalTexts = {
  [ModalState.PROCESSING]: {
    title: "Updating Course Dates",
    ariaLabel: "Updating Course Dates",
    warningText: "Please wait while we update the course dates",
    actionButtonText: "Close",
  },
  [ModalState.SUCCESS]: {
    title: "Course Dates Updated",
    ariaLabel: "Course Dates Updated",
    warningText: "Course dates have been updated",
    actionButtonText: "Close",
  },
  [ModalState.ERROR]: {
    title: "Error Updating Course Dates",
    ariaLabel: "Error Updating Course Dates",
    warningText: "An error occurred while updating the course dates",
    actionButtonText: "Close",
  },
  [ModalState.GENERATION_ERROR]: {
    title: "Error Generating Due Dates",
    ariaLabel: "Error Generating Due Dates",
    warningText: "An error occurred while generating the due dates",
    actionButtonText: "Close",
  },
};

const schema = z
  .object({
    startDate: z.date().nullable(),
    endDate: z.date().nullable(),
    dueDateStartDate: z.date().nullable(),
    dueDateEndDate: z.date().nullable(),
  })
  .refine(
    (data) => {
      // Only validate if both dates exist
      if (!data.startDate || !data.endDate) return true;
      return new Date(data.startDate) <= new Date(data.endDate);
    },
    {
      message: "Start date must be before or equal to end date",
      path: ["startDate", "endDate"],
    }
  )
  .refine(
    (data) => {
      // Only validate if both dates exist
      if (!data.dueDateStartDate || !data.dueDateEndDate) return true;
      return new Date(data.dueDateStartDate) <= new Date(data.dueDateEndDate);
    },
    {
      message: "Due Date Start must be before or equal to Due Date End",
      path: ["dueDateStartDate", "dueDateEndDate"],
    }
  );

interface AdminDueDateControlsContainerProps {
  courseId: string;
}

const AdminDueDateControlsContainer: React.FC<AdminDueDateControlsContainerProps> = ({ courseId }) => {
  const { primaryColor } = useAppConfig();
  const { width } = useWindowDimensions();

  const { data: courseData, refetch: refetchCourseData } = useGetCourseCoreQuery({
    variables: {
      id: courseId,
    },
  });

  const [updateCourseDates] = useUpdateCourseDatesMutation();
  const [generateDueDatesForActivities] = useGenerateDueDatesForActivitiesMutation();

  const [isGeneratingDueDates, setIsGeneratingDueDates] = useState(false);
  const [canGenerateDueDates, setCanGenerateDueDates] = useState(false);

  const [modalState, setModalState] = useState<ModalState>(ModalState.CLOSED);

  const { control, handleSubmit, setValue, formState, reset } = useForm<z.infer<typeof schema>>({
    resolver: zodResolver(schema),
    defaultValues: {
      startDate: null,
      endDate: null,
      dueDateStartDate: null,
      dueDateEndDate: null,
    },
  });

  useEffect(() => {
    if (courseData?.getCourseCore?.data?.[0]) {
      const course = courseData.getCourseCore.data[0];

      if (course.startDate) setValue("startDate", new Date(courseData.getCourseCore.data[0].startDate));

      if (course.endDate) setValue("endDate", new Date(courseData.getCourseCore.data[0].endDate));

      if (course.dueDateStartDate)
        setValue("dueDateStartDate", new Date(courseData.getCourseCore.data[0].dueDateStartDate));

      if (course.dueDateEndDate) setValue("dueDateEndDate", new Date(courseData.getCourseCore.data[0].dueDateEndDate));
    }
  }, [courseData]);

  useEffect(() => {
    if (courseData?.getCourseCore?.data?.[0]) {
      const course = courseData.getCourseCore.data[0];
      setCanGenerateDueDates(course.dueDateStartDate && course.dueDateEndDate);
    }
  }, [courseData]);

  const handleUpdateCourseDates = async () => {
    if (formState.isValid) {
      setModalState(ModalState.PROCESSING);
    }

    handleSubmit(async (data) => {
      const result = await updateCourseDates({
        variables: {
          id: courseId,
          input: {
            startDate: data.startDate ? new Date(data.startDate).toISOString() : null,
            endDate: data.endDate ? new Date(data.endDate).toISOString() : null,
            dueDateStartDate: data.dueDateStartDate ? new Date(data.dueDateStartDate).toISOString() : null,
            dueDateEndDate: data.dueDateEndDate ? new Date(data.dueDateEndDate).toISOString() : null,
          },
        },
      });

      if (result.data?.updateCourseDates.success) {
        setModalState(ModalState.SUCCESS);
      } else {
        setModalState(ModalState.ERROR);
      }
      reset();
      await refetchCourseData();
    })();
  };

  const handleGenerateDueDates = async () => {
    setIsGeneratingDueDates(true);

    const result = await generateDueDatesForActivities({
      variables: {
        courseId,
      },
    });

    if (result.data?.generateDueDatesForActivities.success) {
      setIsGeneratingDueDates(false);
    } else {
      setIsGeneratingDueDates(false);
      setModalState(ModalState.GENERATION_ERROR);
    }

    await refetchCourseData();
  };

  const isStacked = width < 1000;

  return (
    <View style={styles.container}>
      <View>
        <View style={[styles.sectionsContainer, isStacked && styles.sectionsContainerStacked]}>
          {/* Course Start and End Dates Section */}
          <View style={[styles.section, !isStacked && styles.sectionRow]}>
            <CustomText text="Course Start and End Dates" size="l" weight="bold" style={styles.sectionTitle} />
            <CustomText
              text="These dates determine when your course becomes available to students and when it will close."
              size="s"
              style={styles.sectionDescription}
            />

            <View style={styles.dateRow}>
              <View style={styles.dateFieldContainer}>
                <CustomText text="Start Date" size="s" weight="medium" style={styles.fieldLabel} />
                <Controller
                  name="startDate"
                  control={control}
                  render={({ field: { onChange, value } }) => (
                    <DatePickerInput
                      locale="en"
                      label={undefined}
                      value={value}
                      onChange={onChange}
                      placeholder="mm/dd/yyyy"
                      inputMode="start"
                      mode="outlined"
                      withDateFormatInLabel={false}
                      outlineColor={"#D1D5DB"}
                      activeOutlineColor={"blue"}
                      inputProps={{
                        color: "#DEDEDE",
                        mode: "outline",
                      }}
                      theme={{
                        colors: "#000000",
                        roundness: 8,
                        opacity: 1,
                      }}
                      style={{ maxHeight: 50, alignContent: "center", justifyContent: "center" }}
                    />
                  )}
                />
              </View>

              <View style={styles.dateFieldContainer}>
                <CustomText text="End Date" size="s" weight="medium" style={styles.fieldLabel} />
                <Controller
                  name="endDate"
                  control={control}
                  render={({ field: { onChange, value } }) => (
                    <DatePickerInput
                      locale="en"
                      label=""
                      value={value}
                      onChange={onChange}
                      placeholder="mm/dd/yyyy"
                      inputMode="start"
                      mode="outlined"
                      withDateFormatInLabel={false}
                      outlineColor={"#D1D5DB"}
                      activeOutlineColor={"blue"}
                      inputProps={{
                        color: "#DEDEDE",
                        mode: "outline",
                      }}
                      theme={{
                        colors: "#000000",
                        roundness: 8,
                        opacity: 1,
                      }}
                      style={{ maxHeight: 50, alignContent: "center", justifyContent: "center" }}
                    />
                  )}
                />
              </View>
            </View>

            <CustomButton
              text="Set Course Dates"
              styleType="primaryLight"
              size="m"
              onPress={handleUpdateCourseDates}
              style={styles.fullWidthButton}
              leftIcon={
                <Calendar
                  size={24}
                  weight="bold"
                  color={formState.isValid && formState.isDirty ? primaryColor : "#D1D5DB"}
                />
              }
              textStyle={{
                color: formState.isValid && formState.isDirty ? primaryColor : "#D1D5DB",
              }}
              disabled={!formState.isValid || !formState.isDirty}
            />
          </View>

          {/* Update Due Dates Section */}
          <View style={[styles.section, !isStacked && styles.sectionRow]}>
            <CustomText
              text="Update Due Dates for Course Activities"
              size="l"
              weight="bold"
              style={styles.sectionTitle}
            />
            <CustomText
              text="Based on your selected start and end dates, Axio will automatically generate evenly spaced due dates across all modules and activities. You can adjust any due date manually afterward."
              size="s"
              style={styles.sectionDescription}
            />

            <View style={styles.dateRow}>
              <View style={styles.dateFieldContainer}>
                <CustomText text="Start Due Date" size="s" weight="medium" style={styles.fieldLabel} />
                <Controller
                  name="dueDateStartDate"
                  control={control}
                  render={({ field: { onChange, value } }) => (
                    <DatePickerInput
                      locale="en"
                      label=""
                      value={value}
                      onChange={onChange}
                      placeholder="mm/dd/yyyy"
                      inputMode="start"
                      mode="outlined"
                      withDateFormatInLabel={false}
                      outlineColor={"#D1D5DB"}
                      activeOutlineColor={"blue"}
                      inputProps={{
                        color: "#DEDEDE",
                        mode: "outline",
                      }}
                      theme={{
                        colors: "#000000",
                        roundness: 8,
                        opacity: 1,
                      }}
                      style={{ maxHeight: 50, alignContent: "center", justifyContent: "center" }}
                    />
                  )}
                />
              </View>

              <View style={styles.dateFieldContainer}>
                <CustomText text="End Due Date" size="s" weight="medium" style={styles.fieldLabel} />
                <Controller
                  name="dueDateEndDate"
                  control={control}
                  render={({ field: { onChange, value } }) => (
                    <DatePickerInput
                      locale="en"
                      label=""
                      value={value}
                      onChange={onChange}
                      placeholder="mm/dd/yyyy"
                      inputMode="start"
                      mode="outlined"
                      withDateFormatInLabel={false}
                      outlineColor={"#D1D5DB"}
                      activeOutlineColor={"blue"}
                      inputProps={{
                        color: "#DEDEDE",
                        mode: "outline",
                      }}
                      theme={{
                        colors: "#000000",
                        roundness: 8,
                        opacity: 1,
                      }}
                      style={{ maxHeight: 50, alignContent: "center", justifyContent: "center" }}
                    />
                  )}
                />
              </View>
            </View>

            <CustomButton
              text="Set Due Date Range"
              styleType="primaryLight"
              size="m"
              onPress={handleUpdateCourseDates}
              style={styles.fullWidthButton}
              leftIcon={
                <CalendarBlankIcon
                  size={16}
                  weight="bold"
                  color={formState.isValid && formState.isDirty ? primaryColor : "#D1D5DB"}
                />
              }
              textStyle={{
                color: formState.isValid && formState.isDirty ? primaryColor : "#D1D5DB",
              }}
              disabled={!formState.isValid || !formState.isDirty}
            />

            <CustomSpacing type="vertical" size="xl" />

            {isGeneratingDueDates ? (
              <CustomButton
                leftIcon={<ActivityIndicator size="small" color={primaryColor} />}
                text="Generating..."
                size="m"
                styleType="primaryLight"
                style={styles.fullWidthButton}
                disabled={true}
                onPress={() => {}}
              />
            ) : (
              <CustomButton
                text="Generate Due Dates"
                styleType="primaryLight"
                size="m"
                onPress={handleGenerateDueDates}
                style={styles.fullWidthButton}
                leftIcon={
                  <SparkleIcon
                    size={16}
                    weight="bold"
                    color={canGenerateDueDates && !formState.isDirty ? primaryColor : "#D1D5DB"}
                  />
                }
                textStyle={{
                  color: canGenerateDueDates && !formState.isDirty ? primaryColor : "#D1D5DB",
                }}
                disabled={!canGenerateDueDates || formState.isDirty}
              />
            )}
            <CustomSpacing type="vertical" size="l" />
            <CustomText
              text="This action regenerates due dates for all activities and overwrites all existing due dates"
              size="s"
              style={styles.warningText}
            />
          </View>
        </View>

        {!formState.isValid && formState.isDirty && (
          <CustomText
            text="Please check your dates and try again. Start Date should be before End Date, and Start Due Date should be before End Due Date."
            size="xs"
            style={styles.validationError}
          />
        )}
      </View>

      <NewCustomAxioActionModal
        visible={modalState === ModalState.PROCESSING}
        onClose={() => setModalState(ModalState.CLOSED)}
        modalTexts={modalTexts.PROCESSING}
      />
      <NewCustomAxioActionModal
        visible={modalState === ModalState.SUCCESS}
        onClose={() => setModalState(ModalState.CLOSED)}
        modalTexts={modalTexts.SUCCESS}
      />
      <NewCustomAxioActionModal
        visible={modalState === ModalState.ERROR}
        onClose={() => setModalState(ModalState.CLOSED)}
        modalTexts={modalTexts.ERROR}
      />
      <NewCustomAxioActionModal
        visible={modalState === ModalState.GENERATION_ERROR}
        onClose={() => setModalState(ModalState.CLOSED)}
        modalTexts={modalTexts.GENERATION_ERROR}
      />
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
    padding: 24,
    backgroundColor: "#fff",
  },
  sectionsContainer: {
    flexDirection: "row",
    gap: 32,
  },
  sectionsContainerStacked: {
    flexDirection: "column",
    gap: 32,
  },
  section: {
    flex: 1,
    minWidth: 0,
  },
  sectionRow: {
    flex: 1,
  },
  sectionTitle: {
    marginBottom: 8,
    color: "#475467",
  },
  sectionDescription: {
    color: "#6B7280",
    marginBottom: 24,
    lineHeight: 20,
  },
  dateRow: {
    flexDirection: "row",
    gap: 16,
    marginBottom: 24,
  },
  dateFieldContainer: {
    flex: 1,
    minWidth: 0,
  },
  fieldLabel: {
    marginBottom: 8,
    color: "#374151",
  },
  fullWidthButton: {
    width: "100%",
    justifyContent: "center",
  },
  warningText: {
    color: "#667085",
    marginBottom: 16,
  },
  validationError: {
    color: "#EF4444",
    marginTop: 16,
    textAlign: "center",
  },
});

export default AdminDueDateControlsContainer;
